import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Sparkles } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useMenu } from '../src/contexts/MenuContext';
import { useSettings } from '../src/contexts/SettingsContext';
import { DEFAULT_MENU_ITEM_IMAGE } from '../src/constants';
import { t } from '../translations';
import type { Language, MenuItem } from '../types';
import { haptic } from '../src/utils/haptics';

interface Props {
  language: Language;
  /** Скрывать когда чекаут «активен» (showConfirm) — не отвлекать модалом. */
  hidden?: boolean;
}

/**
 * Блок «К этому часто берут» под содержимым корзины. Шлёт current cart
 * на /api/ai/upsell, получает до 3 id из меню, показывает мини-карточки.
 * Запрашивается с debounce 1500мс, чтобы не дёргать Claude на каждый +/-.
 */
const CartUpsell: React.FC<Props> = ({ language, hidden }) => {
  const { cart, addToCart } = useCart();
  const { menuData } = useMenu();
  const { settings } = useSettings();
  const [suggestions, setSuggestions] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Серверу шлём упрощённый ключ — если корзина по сути та же, не пере-запрашиваем.
  const lastSigRef = useRef<string>('');

  useEffect(() => {
    if (hidden || cart.length === 0) {
      setSuggestions([]);
      return;
    }
    const sig = cart.map(i => `${i.id}x${i.quantity}`).sort().join('|');
    if (sig === lastSigRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      lastSigRef.current = sig;
      setLoading(true);
      try {
        const res = await fetch('/api/ai/upsell', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cart: cart.map(i => ({ id: i.id, names: i.names, quantity: i.quantity })),
            language,
          }),
          signal: ac.signal,
        });
        if (!res.ok) throw new Error('upsell ' + res.status);
        const data = await res.json();
        const ids: number[] = Array.isArray(data?.ids) ? data.ids : [];
        const cartIds = new Set(cart.map(i => i.id));
        const items: MenuItem[] = [];
        const allItems = menuData.flatMap(c => c.items);
        for (const id of ids) {
          if (cartIds.has(id)) continue;
          const found = allItems.find(it => it.id === id && it.inStock !== false);
          if (found) items.push(found);
        }
        setSuggestions(items);
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          console.error('upsell error:', e?.message || 'unknown');
        }
      } finally {
        setLoading(false);
      }
    }, 1500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [cart, language, hidden, menuData]);

  useEffect(() => () => abortRef.current?.abort(), []);

  if (hidden || suggestions.length === 0) return null;

  return (
    <div className="space-y-3 pt-4 border-t border-[var(--bg-tertiary)]">
      <div className="flex items-center gap-2 px-2">
        <div className="w-7 h-7 rounded-full bg-[var(--accent-10)] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-[var(--accent-color)]" />
        </div>
        <p className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest">
          {t('cart.upsellTitle', language)}
          {loading && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[var(--accent-color)] animate-pulse"></span>}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <AnimatePresence>
          {suggestions.map(item => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="flex items-center gap-3 bg-[var(--bg-primary)] p-2 rounded-2xl border border-[var(--glass-border)]"
            >
              <img
                src={item.image || DEFAULT_MENU_ITEM_IMAGE}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_MENU_ITEM_IMAGE; }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[var(--text-primary)] truncate">
                  {item.names[language] || item.names.ru}
                </p>
                <p className="text-xs font-bold text-[var(--accent-color)]">
                  {item.price} {settings.currency}
                </p>
              </div>
              <button
                onClick={() => {
                  haptic.light();
                  addToCart(item);
                  setSuggestions(prev => prev.filter(p => p.id !== item.id));
                }}
                className="w-9 h-9 rounded-xl bg-[var(--accent-color)] text-white flex items-center justify-center active:scale-90 transition"
                aria-label="add upsell"
              >
                <Plus className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CartUpsell;
